<template>
  <div>
    <button v-if="!hideTrigger && !inline" type="button" @click="openModal" class="parent-modal-button">
      Manage Entity Parents
    </button>

    <div v-if="isModalOpen || inline" :class="inline ? '' : 'fixed inset-0 z-50 flex items-center justify-center bg-gray-800/60 p-5'" @click.self="!inline && closeModal()" @keydown.esc="handleEscape" @keydown.tab="!inline && trapFocus($event)">
      <div ref="dialog" :role="inline ? 'region' : 'dialog'" :aria-modal="inline ? undefined : true" aria-label="Manage entity parents" class="text-[11px] leading-4" :class="inline ? 'w-full' : 'flex max-h-[80vh] w-full max-w-xl flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl'">
        <div v-if="!inline" class="flex shrink-0 items-start justify-between gap-2 border-b border-gray-200 bg-gray-25 px-3 py-2">
          <div class="min-w-0">
            <h3 class="text-xs font-semibold text-gray-900">Manage parents</h3>
            <p v-if="entityName" class="sensitive mt-1 break-words text-[11px] text-gray-600">{{ entityName }}</p>
          </div>
          <button ref="closeButton" type="button" @click="closeModal" class="parent-modal-button !w-6 !p-0" aria-label="Close parent editor" title="Close">
            <svg class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
              <path stroke-linecap="round" d="M6 6l12 12M6 18L18 6" />
            </svg>
          </button>
        </div>

        <div :class="inline ? '' : 'min-h-0 overflow-y-auto p-4'">
          <section :class="readOnly ? '' : 'mb-4'">
            <div v-if="!inline" class="mb-2 flex items-center justify-between">
              <h4 class="font-semibold text-gray-800">Current parents</h4>
              <span v-if="dataLoaded" class="tabular-nums text-gray-500">{{ currentParents.length }}</span>
            </div>
            <LoadingIndicator v-if="processing && !dataLoaded" message="Loading parents…" />
            <div v-else class="overflow-hidden rounded-lg border border-gray-200">
              <table class="w-full table-fixed text-[11px] leading-4">
                <colgroup><col style="width: 60%" /><col :style="{ width: readOnly ? '40%' : '28%' }" /><col v-if="!readOnly" style="width: 12%" /></colgroup>
                <thead class="border-b border-gray-200 bg-gray-25 text-left text-[11px] text-gray-500">
                  <tr><th scope="col" class="px-2 py-1 font-medium">Entity</th><th scope="col" class="px-2 py-1 font-medium">Type</th><th v-if="!readOnly" scope="col" class="px-2 py-1"><span class="sr-only">Actions</span></th></tr>
                </thead>
                <tbody class="divide-y divide-gray-200">
                  <tr v-for="parent in currentParents" :key="parent.id" class="align-middle">
                    <td class="sensitive break-words px-3 py-1.5 font-medium text-gray-800">{{ parent.name }}</td>
                    <td class="break-words px-3 py-1.5 text-gray-600">{{ parent.entity_type_name }}</td>
                    <td v-if="!readOnly" class="px-3 py-1.5 text-right">
                      <button type="button" class="parent-modal-button parent-modal-remove !w-6 !p-0" @click="removeParent(parent.id)" :disabled="processing" aria-label="Remove parent" title="Remove parent">
                        <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13M10 11v5M14 11v5" /></svg>
                      </button>
                    </td>
                  </tr>
                  <tr v-if="!currentParents.length"><td :colspan="readOnly ? 2 : 3" class="px-2 py-2 text-gray-500">No parents assigned.</td></tr>
                </tbody>
              </table>
            </div>
          </section>

          <div v-if="inline && !readOnly" class="mt-2 flex justify-end">
            <button type="button" class="parent-modal-button" :disabled="processing" @click="openModal">Add parent</button>
          </div>
          <Teleport to="body" :disabled="!inline">
            <div v-if="!readOnly && (!inline || isModalOpen)" :class="inline ? 'fixed inset-0 z-50 flex items-center justify-center bg-gray-800/60 p-5' : ''" @click.self="inline && closeModal()" @keydown.esc="inline && handleEscape($event)" @keydown.tab="inline && trapFocus($event)">
              <div ref="addDialog" :role="inline ? 'dialog' : undefined" :aria-modal="inline ? true : undefined" :aria-label="inline ? 'Add parent' : undefined" :class="inline ? 'w-full max-w-xl overflow-hidden rounded-lg border border-gray-200 bg-white text-[11px] leading-4 shadow-xl' : ''">
                <div v-if="inline" class="flex items-start justify-between gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2">
                  <div class="min-w-0">
                    <h3 class="text-xs font-semibold text-gray-900">Add parent</h3>
                    <p v-if="entityName" class="sensitive mt-1 break-words text-[11px] text-gray-600">{{ entityName }}</p>
                  </div>
                  <button ref="addCloseButton" type="button" class="parent-modal-button !w-6 !p-0" aria-label="Close add-parent dialog" @click="closeModal">
                    <svg class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path stroke-linecap="round" d="M6 6l12 12M6 18L18 6" /></svg>
                  </button>
                </div>
                <section :class="inline ? 'p-4' : 'border-t border-gray-200 pt-3'">
                  <h4 v-if="!inline" class="mb-2 font-semibold text-gray-800">Add a parent</h4>
            <form @submit.prevent="submitForm" class="flex flex-col gap-2 sm:flex-row sm:items-end">
              <label class="flex min-w-0 flex-col gap-1 text-gray-600 sm:w-36 sm:shrink-0">
                Entity type
                <select v-model="selectedEntityType" class="parent-modal-select" :disabled="processing">
                  <option value="">All entity types</option>
                  <option v-for="type in availableEntityTypes" :key="type.id" :value="type.id">{{ type.type_name }}</option>
                </select>
              </label>
              <label class="flex min-w-0 flex-1 flex-col gap-1 text-gray-600">
                Parent entity
                <select v-model="selectedEntity" class="parent-modal-select" :class="{ sensitive: !!selectedEntity }" :disabled="processing || filteredAvailableEntities.length === 0">
                  <option value="">{{ selectedEntityType ? 'Select an entity…' : 'Select a type first…' }}</option>
                  <option v-for="entity in filteredAvailableEntities" :key="entity.id" :value="entity.id" class="sensitive">{{ entity.name }}</option>
                </select>
              </label>
              <button type="submit" class="parent-modal-button parent-modal-primary shrink-0 self-start sm:self-auto" :disabled="!selectedEntity || processing">Add</button>
            </form>
                  <div v-if="inline && message" role="status" class="mt-2 rounded border px-2 py-1 text-[11px]" :class="messageType === 'alert-danger' ? 'border-red-200 bg-red-50 text-red-700' : 'border-gray-200 bg-gray-50 text-gray-700'">{{ message }}</div>
                </section>
              </div>
            </div>
          </Teleport>

          <div v-if="message && !(inline && isModalOpen)" role="status" class="mt-2 rounded border px-2 py-1 text-[11px]" :class="messageType === 'alert-danger' ? 'border-red-200 bg-red-50 text-red-700' : 'border-gray-200 bg-gray-25 text-gray-700'">{{ message }}</div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';

export default {
  name: 'FormManageGroupParticipation',
  components: { LoadingIndicator },

  emits: ['updated', 'closed', 'loaded'],

  props: {
    inline: { type: Boolean, default: false },
    readOnly: { type: Boolean, default: false },
    hideTrigger: { type: Boolean, default: false },
    initiallyOpen: { type: Boolean, default: false },
    entityName: { type: String, default: '' },
    entityId: {
      type: Number,
      required: true
    }
  },

  data() {
    return {
      isModalOpen: false,
      currentParents: [],
      availableEntities: [],
      availableEntityTypes: [],
      selectedEntity: '',
      selectedEntityType: '',
      processing: false,
      message: '',
      messageType: 'alert-info',
      dataLoaded: false,
      hasChanges: false
    };
  },

  computed: {
    availableEntitiesFiltered() {
      const currentParentIds = this.currentParents.map(parent => parent.id);
      // Filter out current parents and self
      return this.availableEntities.filter(entity =>
        !currentParentIds.includes(entity.id) && entity.id !== this.entityId
      );
    },

    filteredAvailableEntities() {
      let entities = this.availableEntitiesFiltered;

      if (this.selectedEntityType) {
        entities = entities.filter(entity =>
          entity.entity_type === parseInt(this.selectedEntityType)
        );
      }

      return entities;
    }
  },

  mounted() {
    if (this.inline) this.fetchData();
    else if (this.initiallyOpen) this.openModal();
  },
  beforeUnmount() {
    if (this.isModalOpen) document.body.style.overflow = this._previousBodyOverflow || '';
  },
  methods: {
    handleEscape(event) {
      if (this.inline && !this.isModalOpen) return;
      event.preventDefault();
      event.stopPropagation();
      this.closeModal();
    },
    notifyParentChange() {
      if (this.inline) this.$emit('updated', [...this.currentParents]);
      else this.hasChanges = true;
    },
    trapFocus(event) {
      const dialog = this.inline ? this.$refs.addDialog : this.$refs.dialog;
      const elements = [...dialog.querySelectorAll('button:not(:disabled), select:not(:disabled), input:not(:disabled), [href], [tabindex="0"]')].filter(element => element.getClientRects().length);
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    },
    openModal() {
      this._previousBodyOverflow = document.body.style.overflow;
      this._returnFocus = document.activeElement;
      this.isModalOpen = true;
      this.$nextTick(() => (this.inline ? this.$refs.addCloseButton : this.$refs.closeButton)?.focus());
      document.body.style.overflow = 'hidden';
      this.selectedEntityType = '';
      this.selectedEntity = '';
      this.message = '';

      if (!this.dataLoaded) {
        this.fetchData();
      }
    },

    closeModal() {
      this.isModalOpen = false;
      document.body.style.overflow = this._previousBodyOverflow || '';
      this.message = '';
      if (this.hasChanges) {
        this.$emit('updated');
        this.hasChanges = false;
      }
      if (this._returnFocus?.isConnected) this._returnFocus.focus();
      this.$emit('closed');
    },

    async fetchData() {
      this.processing = true;
      try {
        // Fetch current parent entities
        const parentsResponse = await axios.get(`/api/entities/${this.entityId}/parents/`);
        this.currentParents = parentsResponse.data;

        // Fetch entity types for filtering
        if (!this.readOnly) {
          const typesResponse = await axios.get('/api/entity-types/');
          this.availableEntityTypes = typesResponse.data;
        }

        this.dataLoaded = true;
      } catch (error) {
        this.showMessage('Error fetching data: ' + (error.response?.data?.detail || error.message), 'alert-danger');
      } finally {
        this.processing = false;
      }
      if (this.dataLoaded) this.$emit('loaded', [...this.currentParents]);
    },

    async submitForm() {
      if (this.readOnly || !this.selectedEntity) return;

      this.processing = true;

      try {
        await axios.post(`/api/entities/${this.entityId}/add-parent/`, {
          parent_id: this.selectedEntity
        });

        this.showMessage('Successfully added parent entity', 'alert-success');
        this.selectedEntity = '';

        // Refresh parents list
        const parentsResponse = await axios.get(`/api/entities/${this.entityId}/parents/`);
        this.currentParents = parentsResponse.data;
        this.notifyParentChange();
        if (this.inline) this.closeModal();
      } catch (error) {
        if (error.response?.data?.error && error.response.data.error.includes('cycle')) {
          this.showMessage('Cannot add parent: would create cycle in hierarchy', 'alert-danger');
        } else {
          this.showMessage('Error adding parent: ' + (error.response?.data?.error || error.message), 'alert-danger');
        }
      } finally {
        this.processing = false;
      }
    },

    async removeParent(parentId) {
      if (this.readOnly) return;
      this.processing = true;
      try {
        await axios.post(`/api/entities/${this.entityId}/remove-parent/`, {
          parent_id: parentId
        });

        this.showMessage('Successfully removed parent entity', 'alert-success');

        // Refresh parents list
        const parentsResponse = await axios.get(`/api/entities/${this.entityId}/parents/`);
        this.currentParents = parentsResponse.data;
        this.notifyParentChange();
      } catch (error) {
        this.showMessage('Error removing parent: ' + (error.response?.data?.error || error.message), 'alert-danger');
      } finally {
        this.processing = false;
      }
    },

    showMessage(text, type = 'alert-info') {
      this.message = text;
      this.messageType = type;

      setTimeout(() => {
        this.message = '';
      }, 5000);
    }
  },

  watch: {
    async selectedEntityType(newValue) {
      this.selectedEntity = '';
      this.availableEntities = [];

      if (newValue) {
        this.processing = true;
        try {
          // Fetch entities filtered by selected type
          const response = await axios.get('/api/entities/', {
            params: { entity_type_id: newValue }
          });
          this.availableEntities = response.data;
        } catch (error) {
          this.showMessage('Error fetching entities: ' + (error.response?.data?.detail || error.message), 'alert-danger');
        } finally {
          this.processing = false;
        }
      }
    }
  }
};
</script>

<style scoped>
@reference "../../assets/theme.css";

.parent-modal-button {
  @apply inline-flex items-center justify-center rounded border border-gray-300 bg-white h-6 px-2 text-[11px] font-medium leading-4 text-gray-700 transition-colors hover:border-gray-400 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-neutral-500 disabled:cursor-not-allowed disabled:opacity-50;
}
.parent-modal-primary {
  @apply border-primary-500 bg-primary-500 text-white hover:border-primary-600 hover:bg-primary-600;
}
.parent-modal-remove {
  @apply hover:border-red-300 hover:bg-red-50 hover:text-red-700;
}
.parent-modal-select {
  @apply w-full rounded border border-gray-300 bg-white h-6 px-2 py-0 text-[11px] leading-4 text-gray-800 focus:outline-none focus:ring-1 focus:ring-neutral-500 disabled:opacity-50;
}
</style>
